import { defineStore } from 'pinia';
import axiosClient from '@/axiosClient';
import { API_ENDPOINTS } from '@/apiConfig';

let pendingEmployeesRequest = null;
let requestGeneration = 0;

export const useEmployeesStore = defineStore('employees', {
  state: () => ({
    employeesList: [],
    employeesLoading: false,
    employeesError: null,
    employeesLoaded: false,
  }),

  getters: {
    hasEmployees: (state) => state.employeesList.length > 0,
    employeeById: (state) => (employeeId) => (
      state.employeesList.find(employee => employee.employee_id === employeeId) || null
    ),
    employeeNames: (state) => state.employeesList.map(employee => ({
      employee_id: employee.employee_id,
      employee_name: employee.employee_name,
    })),
  },

  actions: {
    async fetchEmployees({ force = false } = {}) {
      if (!force && this.employeesLoaded) {
        return this.employeesList;
      }

      if (pendingEmployeesRequest) {
        return pendingEmployeesRequest;
      }

      const currentGeneration = requestGeneration;
      this.employeesLoading = true;
      this.employeesError = null;

      const request = (async () => {
        try {
          const url = API_ENDPOINTS.fetchAllEmployees
            .replace('{employeeId}', 0)
            .replace('{companyId}', 0)
            .replace('{archiveStatus}', 0);

          const response = await axiosClient.get(url);

          if (!Array.isArray(response.data)) {
            throw new Error('Invalid employees response: expected an array.');
          }

          const employees = response.data
            .map(employee => ({
              employee_id: employee.employee_id,
              employee_name: employee.employee_name,
              tenant_id: employee.tenant_id,
              employee_company_id: employee.company?.company_id ?? null,
              employee_company_name: employee.company?.company_name ?? null,
              employee_badge_id: employee.employee_badge_id,
              employee_badge_pin: employee.employee_badge_pin,
              employee_contract: employee.employee_contract,
              employee_department: employee.employee_department,
              employee_email: employee.employee_email,
              employee_join_date: employee.employee_join_date,
              employee_status: employee.employee_status,
              employee_photo_url: employee.employee_photo_url,
              employee_contract_date: employee.employee_contract_date,
              employee_extra_bonuses: employee.employee_extra_bonuses,
              employee_phone_number_main: employee.employee_phone_number_main,
              employee_phone_number_secondary: employee.employee_phone_number_secondary,
              employee_next_of_kin: employee.employee_next_of_kin,
              employee_next_of_kin_number: employee.employee_next_of_kin_number,
              employee_cnic: employee.employee_cnic,
              employee_gender: employee.employee_gender,
              employee_address_permanent: employee.employee_address_permanent,
              employee_address_current: employee.employee_address_current,
              employee_father_name: employee.employee_father_name,
              employee_dob: employee.employee_dob,
            }))
            .sort((employeeA, employeeB) => (
              employeeA.employee_name.localeCompare(employeeB.employee_name)
            ));

          if (currentGeneration === requestGeneration) {
            this.employeesList = employees;
            this.employeesLoaded = true;
          }

          return employees;
        } catch (error) {
          if (currentGeneration === requestGeneration) {
            this.employeesError = error.response?.data?.error
              || error.message
              || 'Failed to fetch employees.';
            this.employeesLoaded = false;
          }

          throw error;
        } finally {
          if (currentGeneration === requestGeneration) {
            this.employeesLoading = false;
          }
        }
      })();

      pendingEmployeesRequest = request;

      try {
        return await request;
      } finally {
        if (pendingEmployeesRequest === request) {
          pendingEmployeesRequest = null;
        }
      }
    },

    async ensureEmployees() {
      return this.fetchEmployees();
    },

    async refreshEmployees() {
      return this.fetchEmployees({ force: true });
    },

    clearEmployees() {
      requestGeneration += 1;
      pendingEmployeesRequest = null;
      this.$reset();
    },
  },
});
